import { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowRight, Merge } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import CreatableCombobox from '../CreatableCombobox';
import { extractErrorMessage } from '../../utils/apiErrors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SiteConsolidationDialog = ({ open, onOpenChange, customer, lsds, onConsolidated }) => {
  const [target, setTarget] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { if (open) { setTarget(''); setPreview(null); setError(''); } }, [open]);
  const options = [...new Map((customer.sites || []).filter((s) => s.name).map((s) => [s.name.trim().toLowerCase(), s.name])).values()].sort();
  const run = async (confirm = false) => {
    setBusy(true); setError('');
    try {
      const res = await axios.post(`${API}/customers/${customer.id}/sites/consolidate`, {
        lsds, target_name: target, preview: !confirm, preview_token: confirm ? preview.preview_token : undefined,
      });
      if (!confirm) setPreview(res.data);
      else {
        await onConsolidated();
        onOpenChange(false);
        toast.success(`${lsds.length} LSD${lsds.length === 1 ? '' : 's'} assigned to ${res.data.target_name}`);
      }
    } catch (err) {
      setError(extractErrorMessage(err, 'Unable to consolidate sites'));
      if (err.response?.status === 409) setPreview(null);
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent className="sm:max-w-xl grid-cols-[minmax(0,1fr)] max-h-[90dvh] overflow-y-auto" data-testid="site-consolidation-dialog">
        <DialogHeader>
          <DialogTitle>Consolidate LSDs</DialogTitle>
          <DialogDescription data-testid="site-consolidation-description">LSD values, asset identities and service history stay unchanged. Matching jobs and field events receive the selected site name.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label>Target site name</Label>
          <CreatableCombobox value={target} options={options} noun="site name" placeholder="Select or enter a site name" testid="site-consolidation-target" disabled={busy}
            onChange={(name) => { setTarget(name); setPreview(null); setError(''); }} />
        </div>
        <div className="max-h-56 overflow-y-auto divide-y divide-slate-100 border-y border-slate-200" data-testid="site-consolidation-lsds">
          {(preview?.lsds || lsds.map((lsd) => ({ lsd }))).map((row) => (
            <div key={row.lsd} className="py-2 text-sm min-w-0" data-testid={`site-consolidation-lsd-${encodeURIComponent(row.lsd)}`}>
              <span className="font-mono break-all">{row.lsd}</span>
              {row.previous_names && <div className="flex items-center gap-2 text-xs text-slate-500 min-w-0 mt-1">
                <span className="min-w-0 break-words">{row.previous_names.map((name) => name || 'Unassigned').join(', ')}</span>
                <ArrowRight size={12} className="shrink-0" /><span className="min-w-0 break-words text-slate-900">{preview.target_name}</span>
              </div>}
            </div>
          ))}
        </div>
        {preview && <div className="grid grid-cols-3 gap-3 border-b border-slate-200 pb-4" data-testid="site-consolidation-impact">
          {[['units', 'Assets'], ['jobs', 'Jobs'], ['field_events', 'Field events']].map(([key, label]) => (
            <div key={key} data-testid={`site-consolidation-count-${key}`}><strong className="block text-xl">{preview.affected[key]}</strong><span className="text-xs text-slate-500">{label}</span></div>
          ))}
        </div>}
        {error && <p role="alert" className="text-sm text-red-600" data-testid="site-consolidation-error">{error}</p>}
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" disabled={busy} onClick={() => onOpenChange(false)} data-testid="site-consolidation-cancel">Cancel</Button>
          <Button type="button" disabled={busy || !target.trim() || !lsds.length} onClick={() => run(!!preview)} data-testid={preview ? 'site-consolidation-confirm' : 'site-consolidation-review'}>
            <Merge size={14} className="mr-2" />{busy ? 'Working...' : preview ? 'Confirm consolidation' : 'Review changes'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};